/**
 * Badge Component
 *
 * A badge component for displaying status, labels, and tags.
 */

import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/utils';
import { REPUTATION_SOURCE_COLORS } from '@/constants';
import type { ReputationSource, FlagStatus } from '@/types';

// ============================================================================
// Types
// ============================================================================

export type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'outline';
export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
}

// ============================================================================
// Styles
// ============================================================================

const baseStyles = 'inline-flex items-center font-medium rounded-full';

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-slate-100 text-slate-700',
  success: 'bg-success-100 text-success-700',
  warning: 'bg-warning-100 text-warning-700',
  danger: 'bg-danger-100 text-danger-700',
  info: 'bg-primary-100 text-primary-700',
  outline: 'border border-slate-300 text-slate-600 bg-transparent',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-sm',
  lg: 'px-3 py-1 text-sm',
};

const dotVariantStyles: Record<BadgeVariant, string> = {
  default: 'bg-slate-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
  info: 'bg-primary-500',
  outline: 'bg-slate-400',
};

// ============================================================================
// Badge Component
// ============================================================================

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'md', dot = false, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(baseStyles, variantStyles[variant], sizeStyles[size], className)}
        {...props}
      >
        {dot && (
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full mr-1.5',
              dotVariantStyles[variant]
            )}
          />
        )}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

// ============================================================================
// Flag Status Badge
// ============================================================================

export interface FlagStatusBadgeProps {
  status: FlagStatus;
  size?: BadgeSize;
  className?: string;
}

export function FlagStatusBadge({ status, size = 'md', className }: FlagStatusBadgeProps) {
  const variant = status === 'flagged' ? 'danger' : 'success';
  const label = status === 'flagged' ? 'Flagged' : 'Clean';

  return (
    <Badge variant={variant} size={size} dot className={className}>
      {label}
    </Badge>
  );
}

// ============================================================================
// Reputation Source Badge
// ============================================================================

export interface ReputationSourceBadgeProps {
  source: ReputationSource;
  size?: BadgeSize;
  className?: string;
}

export function ReputationSourceBadge({ source, size = 'sm', className }: ReputationSourceBadgeProps) {
  const color = REPUTATION_SOURCE_COLORS[source];

  return (
    <span
      className={cn(
        baseStyles,
        sizeStyles[size],
        'text-white',
        className
      )}
      style={{ backgroundColor: color }}
    >
      {source}
    </span>
  );
}

// ============================================================================
// Reputation Sources List
// ============================================================================

export interface ReputationSourcesListProps {
  sources: ReputationSource[];
  maxVisible?: number;
  size?: BadgeSize;
  className?: string;
}

export function ReputationSourcesList({
  sources,
  maxVisible = 3,
  size = 'sm',
  className,
}: ReputationSourcesListProps) {
  if (sources.length === 0) {
    return <span className="text-sm text-slate-400">None</span>;
  }

  const visibleSources = sources.slice(0, maxVisible);
  const remainingCount = sources.length - maxVisible;

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {visibleSources.map((source) => (
        <ReputationSourceBadge key={source} source={source} size={size} />
      ))}
      {remainingCount > 0 && (
        <Badge variant="outline" size={size}>
          +{remainingCount} more
        </Badge>
      )}
    </div>
  );
}

// ============================================================================
// Role Badge
// ============================================================================

export interface RoleBadgeProps {
  role: 'admin' | 'member' | 'viewer';
  size?: BadgeSize;
  className?: string;
}

export function RoleBadge({ role, size = 'sm', className }: RoleBadgeProps) {
  const variants: Record<string, BadgeVariant> = {
    admin: 'danger',
    member: 'info',
    viewer: 'default',
  };

  const labels: Record<string, string> = {
    admin: 'Admin',
    member: 'Member',
    viewer: 'Viewer',
  };

  return (
    <Badge variant={variants[role]} size={size} className={className}>
      {labels[role]}
    </Badge>
  );
}

// ============================================================================
// Count Badge
// ============================================================================

export interface CountBadgeProps {
  count: number;
  max?: number;
  variant?: BadgeVariant;
  className?: string;
}

export function CountBadge({ count, max = 99, variant = 'danger', className }: CountBadgeProps) {
  const displayCount = count > max ? `${max}+` : count;

  if (count === 0) return null;

  return (
    <Badge
      variant={variant}
      size="sm"
      className={cn('min-w-[1.25rem] justify-center', className)}
    >
      {displayCount}
    </Badge>
  );
}
